import React, { useEffect, useState } from 'react';
import { X, Trophy, Sparkles } from 'lucide-react';
import AchievementBadge from './AchievementBadge';
import { trackAchievementUnlocked } from '../utils/analytics';

interface Achievement {
  _id: string;
  name: string;
  description: string;
  icon: string;
  tier: 'bronze' | 'silver' | 'gold' | 'platinum';
  imageUrl?: string;
}

interface AchievementPopupProps {
  achievements: Achievement[];
  isVisible: boolean;
  onClose: () => void;
}

export default function AchievementPopup({ achievements, isVisible, onClose }: AchievementPopupProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (isVisible && achievements.length > 0) {
      setIsAnimating(true);
      setShowConfetti(true);
      
      // Track achievement unlocks
      achievements.forEach(achievement => {
        trackAchievementUnlocked(achievement.name);
      });
      
      // Hide confetti after 2 seconds
      const confettiTimer = setTimeout(() => {
        setShowConfetti(false);
      }, 2000);

      return () => {
        clearTimeout(confettiTimer);
      };
    }
  }, [isVisible, achievements, onClose]);

  if (!isVisible || achievements.length === 0) return null;

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'bronze':
        return 'from-amber-500 to-amber-600';
      case 'silver':
        return 'from-gray-400 to-gray-500';
      case 'gold':
        return 'from-yellow-400 to-yellow-500';
      case 'platinum':
        return 'from-purple-500 to-purple-600';
      default:
        return 'from-gray-400 to-gray-500';
    }
  };

  const getTierText = (tier: string) => {
    switch (tier) {
      case 'bronze':
        return 'Bronze Achievement';
      case 'silver':
        return 'Silver Achievement';
      case 'gold':
        return 'Gold Achievement';
      case 'platinum':
        return 'Platinum Achievement';
      default:
        return 'Achievement';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Confetti effect */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-bounce"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            >
              <Sparkles className="w-4 h-4 text-yellow-400" />
            </div>
          ))}
        </div>
      )}
      
      {/* Popup */}
      <div className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all duration-700 ${
        isAnimating ? 'scale-100 opacity-100 rotate-0' : 'scale-95 opacity-0 rotate-12'
      }`}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors z-10"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Content */}
        <div className="p-6">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div className="relative animate-pulse">
                <Trophy className="w-12 h-12 text-yellow-500" />
                <Sparkles className="w-6 h-6 text-yellow-400 absolute -top-2 -right-2 animate-ping" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 animate-fade-in">
              Achievement Unlocked!
            </h2>
            <p className="text-gray-600 dark:text-gray-300 animate-fade-in">
              {achievements.length === 1 
                ? 'You\'ve earned a new achievement!' 
                : `You\'ve earned ${achievements.length} new achievements!`
              }
            </p>
          </div>

          {/* Achievements */}
          <div className="space-y-4">
            {achievements.map((achievement, index) => (
              <div 
                key={achievement._id}
                className={`bg-gradient-to-r ${getTierColor(achievement.tier)} rounded-lg p-4 text-white transform transition-all duration-500 hover:scale-105`}
                style={{
                  animationDelay: `${index * 200}ms`,
                  animation: 'slideInUp 0.5s ease-out forwards'
                }}
              >
                <div className="flex items-center space-x-4">
                  <AchievementBadge 
                    achievement={achievement} 
                    size="large" 
                    showTooltip={false}
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-xs font-medium bg-white bg-opacity-20 px-2 py-1 rounded-full">
                        {getTierText(achievement.tier)}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold mb-1">{achievement.name}</h3>
                    <p className="text-sm opacity-90">{achievement.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Keep hiking to earn more achievements!
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        .animate-fade-in {
          animation: fadeIn 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
} 